/**
 * Push client — modular Firebase SDK v10+.
 *
 * Handles: SW registration → notification permission → FCM token → backend
 * sync. Fails soft if not configured; surfaces clear errors otherwise.
 */
import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { custApi } from "./customerApi";

const SW_PATH = "/firebase-messaging-sw.js";
let _configPromise = null;
let _messaging = null;
let _swReg = null;

async function fetchConfig() {
  if (_configPromise) return _configPromise;
  _configPromise = custApi.get("/me/push/config").then(r => r.data).catch(() => null);
  return _configPromise;
}

export function isPushSupported() {
  return typeof window !== "undefined"
    && "Notification" in window
    && "serviceWorker" in navigator
    && "PushManager" in window;
}

export async function getPushStatus() {
  if (!isPushSupported()) {
    return { supported: false, permission: "denied", enabled: false };
  }
  const cfg = await fetchConfig();
  const configured = !!(cfg?.enabled && cfg?.api_key && cfg?.app_id
                         && cfg?.messaging_sender_id && cfg?.vapid_public_key);
  return {
    supported: true,
    permission: Notification.permission,
    configured,
    enabled: !!(Notification.permission === "granted"
                 && localStorage.getItem("rescigo:fcm:token")),
    config_missing: !!(cfg?.enabled && (!cfg?.api_key || !cfg?.app_id || !cfg?.messaging_sender_id)),
  };
}

async function initFirebase() {
  const cfg = await fetchConfig();
  if (!cfg?.enabled) throw new Error("Push not configured on server");
  if (!cfg.api_key || !cfg.app_id || !cfg.messaging_sender_id) {
    throw new Error("Firebase web SDK keys missing on server");
  }
  if (!cfg.vapid_public_key) throw new Error("VAPID key missing on server");
  if (!(await isSupported())) throw new Error("Messaging not supported on this browser");

  const firebaseConfig = {
    apiKey: cfg.api_key,
    projectId: cfg.project_id,
    appId: cfg.app_id,
    messagingSenderId: cfg.messaging_sender_id,
    authDomain: cfg.auth_domain || `${cfg.project_id}.firebaseapp.com`,
  };
  const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  _messaging = getMessaging(app);

  // Register the SW ourselves so the same instance is passed to getToken
  _swReg = await navigator.serviceWorker.register(SW_PATH);
  // Wait until active so we can post the config for onBackgroundMessage
  if (!_swReg.active) {
    await new Promise(res => {
      const sw = _swReg.installing || _swReg.waiting;
      if (!sw) return res();
      sw.addEventListener("statechange", () => {
        if (sw.state === "activated") res();
      });
    });
  }
  _swReg.active?.postMessage({ type: "FIREBASE_CONFIG", config: firebaseConfig });

  // Foreground messages — forward payload to SW for consistent handling
  onMessage(_messaging, (payload) => {
    try {
      _swReg?.showNotification(
        payload?.notification?.title || "Rigo says",
        {
          body: payload?.notification?.body || "Time for your medicine",
          icon: "/brand/pwa-192.png",
          badge: "/brand/favicon-32.png",
          tag: "rescigo-alarm",
          renotify: true,
          requireInteraction: true,
          vibrate: [200, 100, 200, 100, 400],
          data: { url: payload?.data?.open_url || "/me/health/pills" },
        },
      );
    } catch { /* ignore */ }
  });

  return { messaging: _messaging, reg: _swReg, cfg };
}

export async function enablePush() {
  if (!isPushSupported()) throw new Error("Push not supported in this browser");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notification permission denied");

  const { messaging, reg, cfg } = await initFirebase();
  const token = await getToken(messaging, {
    vapidKey: cfg.vapid_public_key,
    serviceWorkerRegistration: reg,
  });
  if (!token) throw new Error("Could not obtain FCM token — try again");

  localStorage.setItem("rescigo:fcm:token", token);
  await custApi.post("/me/push/register", {
    token, platform: "web",
    device_name: navigator.userAgent.slice(0, 100),
  });
  return { token };
}

export async function disablePush() {
  const token = localStorage.getItem("rescigo:fcm:token");
  if (token) {
    try { await custApi.post("/me/push/unregister", { token }); } catch { /* noop */ }
  }
  localStorage.removeItem("rescigo:fcm:token");
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
    await reg?.unregister();
  } catch { /* noop */ }
}

export async function sendTestPush() {
  return custApi.post("/me/push/test").then(r => r.data);
}
